import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import CircleButton from "@components/buttons/CircleButton";
import DateStamp from "@components/dates/DateStamp";
import TimeRange from "@components/dates/TimeRange";
import styles from "@components/expert/ExpertConsultsPage.module.css";
import Tag from "@components/labels/Tag";
import Nbsp from "@components/Nbsp";
import TextWithIcon from "@components/text/TextWithIcon";
import TextWithAvatar from "@components/TextWithAvatar";
import Tooltip from "@components/Tooltip";
import BodyCopy from "@components/typography/BodyCopy";
import InternalLink from "@components/typography/InternalLink";
import useMyExpert from "@hooks/useMyExpert";
import { ConsultTimePeriodEnum, ExpertGlobalRoleEnum, GetExpertConsultsByAttributeQuery } from "@typing/Generated";
import { languageNameForCode } from "@utils/languageUtils";
import { getCareProfileName } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { consultStartsInFuture } from "@utils/timeUtils";

type ConsultType = NonNullable<
  NonNullable<NonNullable<GetExpertConsultsByAttributeQuery["expertConsults"]["edges"]>[number]>["node"]
>;

type Props = {
  consult: ConsultType;
  date: Date;
  dateByString: (date: Date) => string;
  onReassign: (consult: ConsultType) => void;
  upcoming?: ConsultTimePeriodEnum | null;
};

const ExpertConsultRow = ({ consult, date, dateByString, onReassign, upcoming }: Props) => {
  const myExpert = useMyExpert();
  const intl = useIntl();

  const colorForExpertTag = consult.expert.id === consult.journey.primaryExpert?.id ? "primary-300" : "secondary-500";

  const hasPublishedReport = useMemo((): boolean => {
    if (consult.journey.initialConsultReport.consult?.id === consult.id) {
      return consult.journey.initialConsultReport.published;
    } else {
      return consult.consultReport?.published ?? false;
    }
  }, [consult]);

  return (
    <tr key={consult.id}>
      <td>
        <div className={styles.bigText}>
          <div>{dateByString(consult.startTime)}</div>
          <div>
            <DateStamp showWeekday={false} value={consult.startTime} />
          </div>
        </div>
      </td>
      <td>
        <div
          className={
            upcoming === ConsultTimePeriodEnum.UPCOMING && consult.endTime < date ? styles.disabled : undefined
          }
        >
          <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
        </div>
      </td>
      <td>
        {consult.journey.owner.lastName}
        <FormattedMessage id="dictionary.punctuation.comma" />
        <Nbsp />
        {consult.journey.owner.firstName}
      </td>
      <td>
        <div className={styles.language}>
          {consult.languageCode && (
            <TextWithIcon icon="commentAltPlus" iconFirst text={languageNameForCode(consult.languageCode)} />
          )}
        </div>
      </td>
      <td>
        <InternalLink
          className={styles.bigText}
          to={journeyRoute({ journeyId: consult.journey.id, linkAfterJourney: "", myExpert })}
        >
          {getCareProfileName({ intl, journey: consult.journey })}
        </InternalLink>
      </td>
      <td>
        <div className={styles.roles}>
          {myExpert?.id === consult.expert.id ? (
            <Tag
              color={colorForExpertTag}
              text={intl.formatMessage(
                {
                  id: `pages.expert.myConsult`
                },
                {
                  firstName: consult.expert.firstName
                }
              )}
            />
          ) : (
            <TextWithAvatar
              color={colorForExpertTag}
              photoUrl={consult.expert.photoUrl}
              text={consult.expert.firstName}
            />
          )}
        </div>
      </td>
      <td>
        {consultStartsInFuture(consult) ? (
          <BodyCopy>
            <FormattedMessage id="models.consult.notStarted" />
          </BodyCopy>
        ) : (
          <>
            {hasPublishedReport ? (
              <Tag color="success">
                <FormattedMessage id="models.consult.finalized" />
              </Tag>
            ) : (
              <InternalLink
                stylized={false}
                to={journeyRoute({
                  journeyId: consult.journey.id,
                  linkAfterJourney: `/care-profile?docSection=sessions`,
                  myExpert
                })}
              >
                <Tag color="danger">
                  <FormattedMessage id="models.consult.notFinalized" />
                </Tag>
              </InternalLink>
            )}
          </>
        )}
      </td>
      {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER && consult.endTime > new Date() && (
        <td>
          <Tooltip content={<FormattedMessage id="dictionary.reassign" />}>
            {tooltipTriggerProps => (
              <CircleButton
                ariaLabelKey="accessibility.ariaLabels.reassign"
                fill="clear"
                icon="peopleArrows"
                onClick={() => onReassign(consult)}
                size="medium"
                {...tooltipTriggerProps}
              />
            )}
          </Tooltip>
        </td>
      )}
    </tr>
  );
};

export default ExpertConsultRow;
